import { SxProps, Theme } from '@mui/material/styles';
import { CSSProperties } from 'react';

const rainbowColorVars = {
  '--color-1': '0 100% 55%',
  '--color-2': '30 100% 58%',
  '--color-3': '60 100% 60%',
  '--color-4': '120 100% 50%',
  '--color-5': '200 100% 55%',
  '--color-6': '260 100% 60%',
  '--color-7': '300 100% 60%',
} as const;

const rainbowGradient = `
linear-gradient(
  90deg,
  hsl(var(--color-1)) 0%,
  hsl(var(--color-2)) 14%,
  hsl(var(--color-3)) 28%,
  hsl(var(--color-4)) 42%,
  hsl(var(--color-5)) 56%,
  hsl(var(--color-6)) 70%,
  hsl(var(--color-7)) 84%,
  hsl(var(--color-1)) 100%
)
`;

const rainbowShiftKeyframes = {
  '@keyframes googlePromptRainbowShift': {
    '0%': { backgroundPosition: '0% center' },
    '100%': { backgroundPosition: '300% center' },
  },
} as const;

export const googleAiVoiceStyles = {
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 260,
    width: '100%',
  } as const,
  root: (theme: Theme) =>
    ({
      padding: theme.spacing(2),
      gap: theme.spacing(2),
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      flexGrow: 1,
    }) as CSSProperties,
  headerRow: (theme: Theme) =>
    ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      gap: theme.spacing(2),
      flexWrap: 'wrap',
    }) as CSSProperties,
  contentRow: (theme: Theme) =>
    ({
      display: 'flex',
      flexGrow: 1,
      gap: theme.spacing(2),
      minHeight: 0,
    }) as CSSProperties,
  editorColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    justifyContent: 'space-between',
    minHeight: 0,
  } as const,
  editorSurface: {
    display: 'flex',
    flexGrow: 1,
    position: 'relative',
    minHeight: 0,
  } as const,
  singleSpeakerTextarea: (theme: Theme) =>
    ({
      backgroundColor: theme.palette.bgZero.main,
      padding: theme.spacing(2),
      flexGrow: 1,
      color: theme.palette.text.primary,
      resize: 'none' as const,
      border: 0,
      outline: 0,
      fontSize: 14,
      minHeight: 280,
    }) as CSSProperties,
  singleSpeakerContainer: (theme: Theme) =>
    ({
      display: 'flex',
      flexGrow: 1,
      position: 'relative',
      padding: theme.spacing(1.5),
      paddingTop: theme.spacing(0),
      paddingLeft: theme.spacing(0),
    }) as CSSProperties,
  multiSpeakerContainer: (theme: Theme) =>
    ({
      padding: theme.spacing(1.5),
      paddingTop: theme.spacing(0),
      paddingLeft: theme.spacing(0),
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      overflowY: 'auto' as const,
      minHeight: 140,
    }) as CSSProperties,
  segmentCard: (theme: Theme) =>
    ({
      backgroundColor: theme.palette.bgOne.main,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    }) as CSSProperties,
  segmentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as const,
  segmentTextarea: (theme: Theme) =>
    ({
      backgroundColor: theme.palette.bgZero.main,
      padding: theme.spacing(1),
      color: theme.palette.text.primary,
      resize: 'vertical' as const,
      border: 0,
      outline: 0,
      fontSize: 14,
      minHeight: 48,
    }) as CSSProperties,
  addSegmentRow: (theme: Theme) =>
    ({
      display: 'flex',
      justifyContent: 'center',
      paddingTop: theme.spacing(0.5),
    }) as CSSProperties,
  outputPanel: (theme: Theme) =>
    ({
      flexBasis: 360,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minWidth: 320,
      gap: theme.spacing(2),
    }) as CSSProperties,
  outputColumn: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      minHeight: 0,
    }) as CSSProperties,
  outputControlRow: (theme: Theme) =>
    ({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: theme.spacing(2),
    }) as CSSProperties,
  actionRow: (theme: Theme) =>
    ({
      display: 'flex',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
    }) as CSSProperties,
  actionButtonPrimary: {
    alignSelf: 'baseline',
    minWidth: 170,
  } as const,
  actionButtonSecondary: {
    alignSelf: 'baseline',
    minWidth: 170,
    fontWeight: 700,
  } as const,
  promptContainerSx: (theme: Theme): SxProps<Theme> => ({
    flex: '1 1 auto',

    ...rainbowColorVars,
    '--rainbow-gradient': rainbowGradient,

    '--bg-color-raw': '255,255,255',
    '--bg-color': 'rgb(var(--bg-color-raw))',

    position: 'relative',
    display: 'inline-flex',
    alignItems: 'stretch',
    flexShrink: 0,

    borderRadius: '10px',
    border: '1px solid transparent',
    borderBottomWidth: '2.5px',
    borderColor: 'rgba(229, 231, 235, 0.8)',

    backgroundSize: '300% 100%',
    backgroundClip: 'padding-box, border-box, border-box',
    backgroundOrigin: 'border-box',

    backgroundImage: `
  linear-gradient(var(--bg-color), var(--bg-color)),
  linear-gradient(
    var(--bg-color) 50%,
    rgba(var(--bg-color-raw), 0.8) 80%,
    rgba(var(--bg-color-raw), 0)
  ),
  var(--rainbow-gradient)
`,

    isolation: 'isolate',

    ...rainbowShiftKeyframes,

    animation: 'googlePromptRainbowShift 5s linear infinite',

    '&::before': {
      content: '""',
      position: 'absolute',
      bottom: '-25%',
      left: '50%',
      zIndex: 0,
      height: '20%',
      width: '60%',
      transform: 'translateX(-50%)',
      pointerEvents: 'none',

      background: 'var(--rainbow-gradient)',
      backgroundSize: '300% 100%',

      animation: 'googlePromptRainbowShift 4s linear infinite',

      filter: 'blur(1.6rem) saturate(1.2)',
      opacity: 0.95,
      transition: 'filter 220ms ease',
    },

    '&:hover, &:focus-within': {
      '--bg-color-raw': '235,245,255',
    },

    ...(theme.palette.mode === 'dark' && {
      '--bg-color-raw': '16,18,20',
      borderColor: 'rgba(147, 197, 253, 0.2)',

      '&:hover, &:focus-within': {
        '--bg-color-raw': '13,38,63',
      },

      '&::before': {
        width: '90%',
        filter: 'blur(0.7rem)',
      },
    }),

    '&:focus-within::before': {
      filter: 'blur(1.8rem)',
    },
  }),
  promptTextFieldSx: (theme: Theme): SxProps<Theme> => ({
    position: 'relative',
    zIndex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: '9px',
      backgroundColor: theme.palette.background.paper,
    },
  }),
  modeSwitchOutlineSx: (theme: Theme): SxProps<Theme> => ({
    ...rainbowColorVars,
    '--rainbow-gradient': rainbowGradient,

    '--bg-color-raw': '255,255,255',
    '--bg-color': 'rgb(var(--bg-color-raw))',

    position: 'relative',
    display: 'inline-flex',
    alignItems: 'stretch',
    borderRadius: '5px',
    border: '1px solid transparent',

    backgroundSize: '300% 100%',
    backgroundClip: 'padding-box, border-box',
    backgroundOrigin: 'border-box',
    backgroundImage: `
  linear-gradient(var(--bg-color), var(--bg-color)),
  var(--rainbow-gradient)
`,

    isolation: 'isolate',

    ...rainbowShiftKeyframes,

    animation: 'googlePromptRainbowShift 5s linear infinite',

    '&:hover, &:focus-within': {
      '--bg-color-raw': '235,245,255',
    },

    ...(theme.palette.mode === 'dark' && {
      '--bg-color-raw': '16,18,20',

      '&:hover, &:focus-within': {
        '--bg-color-raw': '13,38,63',
      },
    }),
  }),
  modeSwitchButtonSx: (theme: Theme): SxProps<Theme> => ({
    position: 'relative',
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    textTransform: 'none',
    '&.MuiButton-outlined': {
      borderColor: 'transparent',
    },
    '&.MuiButton-outlined:hover': {
      borderColor: 'transparent',
      backgroundColor: theme.palette.background.paper,
    },
  }),
};
